import { Camera, ImagePlus, Loader2, Lock, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { VisitorProfileField } from "@/lib/graphql/queries/generated/profile.generated";
import { cn } from "@/lib/utils";

/**
 * Centered avatar hero — image (or initials fallback) with a soft
 * branded halo on hover. The avatar circle IS the click target —
 * there's no separate "Change photo" button under it (the hover
 * overlay + the `aria-label` carry the affordance). A floating
 * trash button clears an existing image; pinned avatars hide both
 * the upload and clear affordances and show a small lock badge.
 */
export function AvatarHero({
  field,
  value,
  uploading,
  onPick,
  onClear,
  displayName,
  t,
}: {
  field: VisitorProfileField;
  value: string | null;
  uploading: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
  displayName: string;
  t: (key: string) => string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Track per-URL load failure so a 429 / network blip falls back to
  // initials instead of a broken-image glyph. Reset when `value` flips
  // (e.g. visitor re-uploads after the failure) so the new URL gets a
  // fresh attempt rather than staying stuck on the prior error state.
  const [imgFailed, setImgFailed] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is the deliberate reset trigger — we DO want to clear the failure state when the visitor uploads a new URL, even though the effect body doesn't read it.
  useEffect(() => {
    setImgFailed(false);
  }, [value]);
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
  const pinned = field.pinned;

  return (
    <div className="flex flex-col items-center gap-2.5 pt-4 pb-2">
      <div className="group relative">
        {/* Soft branded halo behind the avatar — uses the operator's
            primary token so re-skinning the widget recolors it for free. */}
        <span
          aria-hidden="true"
          className="absolute -inset-2 rounded-full bg-wx-primary/20 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100"
        />
        <button
          type="button"
          disabled={pinned || uploading}
          onClick={() => inputRef.current?.click()}
          aria-label={t("uploadAvatar")}
          style={{ height: 88, width: 88 }}
          className={cn(
            "relative flex items-center justify-center overflow-hidden rounded-full",
            "bg-gradient-to-br from-wx-bg-elevated to-wx-bg-elevated-2 text-2xl font-semibold text-wx-fg-muted",
            "ring-1 ring-inset ring-wx-border",
            !pinned &&
              !uploading &&
              "cursor-pointer transition-transform duration-200 hover:scale-[1.04]",
            pinned && "cursor-not-allowed",
          )}
        >
          {value && !imgFailed ? (
            // biome-ignore lint/performance/noImgElement: visitor-uploaded image of arbitrary host/dimensions; next/image needs known sizes + an allowlist
            <img
              src={value}
              alt=""
              className="h-full w-full object-cover"
              // Google's `lh3.googleusercontent.com` CDN sometimes 429s
              // when accessed with a localhost / non-https Referer.
              // Strip the referer for visitor-uploaded avatars too —
              // there's no analytics reason to send it, and the image
              // host has no use for it. Defensive against 3rd-party CDN
              // rate-limits and tracking-based throttles in general.
              referrerPolicy="no-referrer"
              onError={() => setImgFailed(true)}
            />
          ) : initials ? (
            <span aria-hidden="true">{initials}</span>
          ) : (
            <Camera size={26} aria-hidden="true" />
          )}
          {!pinned && (
            <span className="absolute inset-x-0 bottom-0 flex h-7 items-center justify-center bg-black/45 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
              {uploading ? (
                <Loader2 size={14} className="animate-spin text-white" aria-hidden="true" />
              ) : (
                <ImagePlus size={14} className="text-white" aria-hidden="true" />
              )}
            </span>
          )}
        </button>
        {pinned && (
          <span className="absolute right-0 bottom-0 flex h-6 w-6 items-center justify-center rounded-full bg-wx-bg shadow-sm ring-1 ring-wx-border">
            <Lock size={11} className="text-wx-fg-subtle" aria-hidden="true" />
          </span>
        )}
        {!pinned && value && (
          <button
            type="button"
            onClick={onClear}
            aria-label={t("removeAvatar")}
            className="-top-1 -right-1 absolute flex h-7 w-7 items-center justify-center rounded-full border border-wx-border bg-wx-bg text-wx-fg-muted shadow-sm transition-colors hover:bg-wx-bg-elevated hover:text-wx-danger"
          >
            <Trash2 size={12} aria-hidden="true" />
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            e.target.value = "";
          }}
        />
      </div>
      {displayName && <p className="text-[15px] font-semibold text-wx-fg">{displayName}</p>}
      {pinned && <p className="text-[11px] text-wx-fg-subtle">{t("pinnedHint")}</p>}
    </div>
  );
}
